<template lang="html">
  <section class="screen-transactions">
    <div class="transactions-wrapper">
      <prt-section-separator :amount="searchSummary.amount" key="searchSummary" :name="`Total: ${searchSummary.name}`" v-if="searchSummary"/>
      <div class="empty-state" :style="{height: searchSummary ? 'calc(100vh - 32px)' : ''}" v-show="Object.keys(searchResults).length === 0">
        <h1>No transactions found.</h1>
      </div>
      <transition-group v-for="(group, key) in searchResults" class="transaction-group" :key="key" name="grow" tag="div">
        <prt-section-separator :amount="getTotal(group)" :key="key" :name="key" />
        <prt-transaction :amount="transaction.amount" :description="transaction.description" :icon="transaction.icon" :key="transaction.id" :timestamp="transaction.createdAt" v-for="transaction in group" @delete="deleteTransaction(transaction.id)"/>
      </transition-group>
    </div>
  </section>
</template>

<script>
import { startOfDay, isToday, isYesterday, format } from 'date-fns';

import PrtButton from './ui/PrtButton.vue';
import PrtSectionSeparator from './ui/PrtSectionSeparator.vue';
import PrtTransaction from './ui/PrtTransaction.vue';

export default {
  components: {
    PrtButton,
    PrtSectionSeparator,
    PrtTransaction,
  },
  computed: {
    matchingTransactions() {
      return this.$store.state.application.transactions;
    },
    searchQuery() {
      return this.$store.state.application.searchQuery;
    },
    searchResults() {
      let transactions;
      if (!this.searchQuery) transactions = this.matchingTransactions;
      else transactions = this.matchingTransactions.slice(0, this.searchResultsAmount);
      // group the results by their creation date
      const reduction = transactions.reduce((accumulator, transaction) => {
        const groups = accumulator;
        let date = startOfDay(transaction.createdAt);

        if (isToday(date)) date = 'today';
        else if (isYesterday(date)) date = 'yesterday';
        else date = format(date, 'DD MMMM YYYY');

        if (!groups[date]) groups[date] = [];
        groups[date].push(transaction);
        return groups;
      }, {});

      return reduction;
    },
    searchSummary() {
      if (!this.searchQuery) return false;

      // let transactions = [];
      //
      // Object.keys(this.searchResults)
      //   .forEach((key) => { transactions = transactions.concat(this.searchResults[key]); });

      const amount = this.getTotal(this.matchingTransactions);

      return {
        name: this.searchQuery,
        amount,
      };
    },
    transactionPage() {
      return this.$store.state.application.transactionPage;
    },
  },
  created() {
    // clear any queries
    this.$store.commit('updateQuery', { query: null });
    this.$store.commit('setTransactionPageSize', this.pageSize);
    this.$store.commit('setTransactionPage', 0);
  },
  data() {
    return {
      initialised: false,
      pageSize: 15,
      searchResultsAmount: 15,
    };
  },
  methods: {
    deleteTransaction(id) {
      this.$store.dispatch('deleteTransaction', id)
        .catch(err => this.$store.commit('addToast', { content: `Could not delete transaction: ${err.message}`, color: 'negative', timeout: 5000 }));
    },
    getTotal(transactions) {
      const reduction = transactions.reduce((total, transaction) => {
        total += transaction.amount; // eslint-disable-line no-param-reassign
        return total;
      }, 0);
      return reduction;
    },
    handleScroll() {
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = window.requestAnimationFrame(() => {
        const pageBottomReached = this.$el.scrollTop + window.innerHeight > this.$el.scrollHeight - 50;
        if (pageBottomReached) {
          if (!this.searchQuery) this.$store.dispatch('loadNextMatchingTransactionsPage', this.pageSize);
          else this.searchResultsAmount += this.pageSize;
        }
      });
    },
    prefillTransactions() {
      // if there’s nowhere to scroll, add the next page already
      if (this.$el.scrollHeight <= this.$el.clientHeight) {
        if (!this.searchQuery) {
          this.$store.dispatch('loadNextMatchingTransactionsPage', this.pageSize)
            .then(() => {
              window.setTimeout(this.prefillTransactions, 200);
            });
        } else {
          this.searchResultsAmount += this.pageSize;
          window.setTimeout(this.prefillTransactions, 200);
        }
      } else this.initialised = true;
    },
  },
  mounted() {
    // add scroll listener
    this.$el.addEventListener('scroll', this.handleScroll);
    // add the first page of transactions to the searchResults
    this.$store.dispatch('initializeMatchingTransactions', this.pageSize)
      .then(() => window.setTimeout(this.prefillTransactions, 200))
      .catch(err => this.$store.commit('addToast', { content: `Could not load matching transactions: ${err.message}`, color: 'negative', timeout: 5000 }));
  },
  name: 'Transactions',
  watch: {
    searchQuery() {
      this.searchResultsAmount = this.pageSize;
      if (this.$el.scrollTop !== 0) this.$el.scrollTop = 0;
      this.$nextTick(() => this.prefillTransactions());
    },
    transactionPage(newVal) {
      if (newVal === 0 && this.initialised) this.prefillTransactions();
    },
  },
};
</script>

<style lang="stylus" scoped>
  @require '../styles/colors'

  .screen-transactions
    height: 100vh
    overflow-x: hidden
    overflow-y: auto

    .transactions-wrapper
      max-width: 1200px
      margin: 0 auto

      .empty-state
        text-align: center
        height: 100vh
        display: flex
        justify-content: center
        align-items: center
        flex-direction: column

        h1
          color: black-38
          font-size: 1rem

      .prt-transaction + .prt-section-separator
        margin-top: 24px

      .transaction-group
        margin-bottom: 24px

        &:last-child
          margin-bottom: 62px

      .grow-enter-active,
      .grow-leave-active
        transition: height 200ms ease, transform 200ms ease

      .grow-move
        transition: transform 200ms ease

      .grow-enter,
      .grow-leave-to
        height: 0

      .grow-leave-to
        transform: translateX(100vw);
</style>
